import { Box, Button, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { FC, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { useChatStore } from "../../../hooks/useChatStore";
import { KeyIcon } from "../../icons";
import LoadingSpinner from "../../ui/LoadingSpinner";
import ChatMessage from "./ChatMessage";
import Column from "../../ui/core/Column";
import StatusMessagesRenderer from "./StatusMessagesRenderer";
import { useChannels } from "../../../contexts/ChannelsContext";
import { useClaimsStore } from "../../../hooks/useClaimsStore";
import { SIDEBAR_TAB_TYPES } from "../../../types/sidebar";
import { useAuth } from "../../../contexts/AuthContext";
import ClaimPredefaultQuestionPreview from "../../claims/components/ClaimPredefaultQuestionPreview";

const ChatSection: FC = observer(() => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { channelId } = useParams<{ channelId: string }>();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const isUserScrollingRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const hasScrolledOnLoadRef = useRef(false);
  const hasSetInitialSidebarTabRef = useRef(false);

  const claimsStore = useClaimsStore();
  const { claimActivePredefaultQuestion } = claimsStore;
  const { updateSidebarSelectedTab } = useChannels();
  const chatStore = useChatStore();
  const {
    activeChatId,
    messages,
    isChatLoading,
    isQuestioning,
    isAborting,
    statusMessages,
    chatDetails,
    chatLoadFailure,
  } = chatStore;
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const messageIdToScroll = searchParams.get("messageId");

  useEffect(() => {
    // Skip the fetch when we already have details for this exact chat — e.g.
    // a chat just created client-side (see ChatStore.askQuestion), which
    // seeds chatDetails/messages itself. Re-fetching here would wipe that
    // local state via clearChatStates() and flash the pane empty.
    if (channelId && chatStore.chatDetails?.id !== channelId) {
      handleLoadData(channelId);
    }
  }, [channelId]);

  const handleLoadData = async (chatId: string) => {
    const chatData = await chatStore.loadChatMessages(chatId);
    if (
      !chatData?.id ||
      !chatData?.user_email ||
      hasSetInitialSidebarTabRef.current
    ) {
      return;
    }

    hasSetInitialSidebarTabRef.current = true;
    const isNotCurrentUser = chatData?.user_email !== user?.email;

    if (chatData?.shared && isNotCurrentUser) {
      updateSidebarSelectedTab(SIDEBAR_TAB_TYPES.shared);
    } else if (chatData?.is_pinned && !isNotCurrentUser) {
      updateSidebarSelectedTab(SIDEBAR_TAB_TYPES.pinned);
    }
  };

  // Smart polling for read-only (guest) viewers with backoff:
  //   Processing: 10s | Idle <2min: 30s | Idle 2-5min: 60s | Idle 5min+: 120s
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!chatStore.isReadOnly || !activeChatId) {
      return;
    }

    let cancelled = false;
    let idlePolls = 0; // consecutive polls with no processing

    const getIdleInterval = () => {
      if (idlePolls < 4) return 30_000; // first ~2 min: every 30s
      if (idlePolls < 10) return 60_000; // 2-5 min: every 60s
      return 120_000; // 5+ min: every 120s
    };

    const schedulePoll = (delayMs: number) => {
      pollTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        const isProcessing = await chatStore.pollNewMessages();
        if (cancelled) return;

        if (isProcessing) {
          idlePolls = 0;
          schedulePoll(10_000);
        } else {
          idlePolls++;
          schedulePoll(getIdleInterval());
        }
      }, delayMs);
    };

    // Start first poll after 10s
    schedulePoll(10_000);

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [activeChatId, chatStore.isReadOnly]);

  // REV-2348 / REV-2422: in-flight watchdog for the chat owner. The websocket is
  // the only thing that clears `isQuestioning` / `isAborting`, and response_worker
  // skips channel delivery when the socket is down (`user_not_connected`). Poll
  // reconciles lock state, backfills missed frames, and clears isAborting when a
  // terminal row arrives. Keep running after Stop too: the lock is released
  // immediately so isQuestioning drops while isAborting stays set until the
  // saved terminal frame is polled.
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (chatStore.isReadOnly || !activeChatId || (!isQuestioning && !isAborting)) {
      return;
    }

    let cancelled = false;

    const scheduleWatchdog = (delayMs: number) => {
      watchdogTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        await chatStore.pollNewMessages();
        if (cancelled) return;
        scheduleWatchdog(20_000);
      }, delayMs);
    };

    // Hold off 30s: a healthy turn is driven by the socket, and this is only
    // meant to catch the case where the terminal frame never arrives.
    scheduleWatchdog(30_000);

    return () => {
      cancelled = true;
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    };
  }, [activeChatId, chatStore.isReadOnly, isQuestioning, isAborting]);

  const scrollToBottom = () => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const scrollToMessage = (messageId: string) => {
    const messageElement = messageRefs.current.get(messageId);
    if (messageElement) {
      messageElement.scrollIntoView({ block: "center" });
      messageElement.style.backgroundColor = "#fef3c7";
      setTimeout(() => {
        messageElement.style.backgroundColor = "";
      }, 2000);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
    setAtTop(scrollTop <= 0);
    setAtBottom(isAtBottom);
    if (isAtBottom) {
      setAutoScrollEnabled(true);
    } else if (isUserScrollingRef.current) {
      setAutoScrollEnabled(false);
    }
    isUserScrollingRef.current = true;
  };

  // Auto-scroll on new messages (only when no messageId to scroll to and auto-scroll is enabled)
  useEffect(() => {
    if (messageIdToScroll || !autoScrollEnabled) return;
    isUserScrollingRef.current = false;
    if (isChatLoading) return;
    if (messages.length === 0) return;

    if (isInitialLoadRef.current && !hasScrolledOnLoadRef.current) {
      hasScrolledOnLoadRef.current = true;
      isInitialLoadRef.current = false;
      const id = requestAnimationFrame(() => scrollToBottom());
      return () => cancelAnimationFrame(id);
    }
    if (isQuestioning) {
      scrollToBottom();
    } else {
      const id = requestAnimationFrame(() => scrollToBottom());
      return () => cancelAnimationFrame(id);
    }
  }, [
    messages,
    statusMessages,
    isQuestioning,
    messageIdToScroll,
    autoScrollEnabled,
    isChatLoading,
  ]);

  // Scroll to specific message when messageId is in URL
  useEffect(() => {
    if (messageIdToScroll && messages.length > 0) {
      const timer = setTimeout(() => scrollToMessage(messageIdToScroll), 100);
      return () => clearTimeout(timer);
    }
  }, [messageIdToScroll, messages]);

  // Reset scroll state when switching chat
  useEffect(() => {
    setAutoScrollEnabled(true);
    isInitialLoadRef.current = true;
    hasScrolledOnLoadRef.current = false;
  }, [activeChatId]);

  // Sync fade visibility with scroll position when content changes
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;

    setAtTop(scrollTop <= 0);
    setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
  }, [messages.length, statusMessages]);

  if (isChatLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingSpinner text="Loading chat..." />
      </Box>
    );
  }

  // Only what the server actually said: an unresolved load falls through.
  if (!isChatLoading && !chatDetails?.id && !isQuestioning) {
    if (chatLoadFailure === "failed") {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1.5,
            px: 3,
          }}
        >
          <Typography variant="body1" color="text.secondary" textAlign="center">
            This case couldn't be loaded. It may still be there — please try
            again.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            data-testid="chat-load-retry"
            onClick={() => channelId && handleLoadData(channelId)}
          >
            Try again
          </Button>
        </Box>
      );
    }

    if (chatLoadFailure === "not_found") {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1.5,
            px: 3,
          }}
        >
          <KeyIcon className="w-10 h-10 text-hex-gray-400" />
          <Typography variant="body1" color="text.secondary" textAlign="center">
            This case doesn't exist or hasn't been shared with you.
          </Typography>
        </Box>
      );
    }
  }

  if (claimActivePredefaultQuestion) {
    return <ClaimPredefaultQuestionPreview hideInput />
  }

  const fadeHeight = 45;
  const fadeBg =
    "linear-gradient(to bottom, var(--fade-color, rgba(243,244,246,1)), rgba(243,244,246,0))";
  const fadeBgBottom =
    "linear-gradient(to top, var(--fade-color, rgba(243,244,246,1)), rgba(243,244,246,0))";

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top fade overlay – fixed at top of scroll area */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: fadeHeight,
          zIndex: 10,
          pointerEvents: "none",
          background: fadeBg,
          opacity: atTop ? 0 : 1,
          transition: "opacity 0.15s ease",
        }}
      />
      {/* Bottom fade overlay – fixed at bottom of scroll area */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: fadeHeight,
          zIndex: 10,
          pointerEvents: "none",
          background: fadeBgBottom,
          opacity: atBottom ? 0 : 1,
          transition: "opacity 0.15s ease",
        }}
      />
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Column
          id="chat-section-column"
          sx={{
            width: "100%",
            minWidth: 0,
            marginTop: "auto",
            padding: "1rem",
            height: "unset",
            paddingTop: "2rem",
            paddingBottom: "0rem",
          }}
        >
          {messages.map((message, index) => (
            <Box
              key={`${message.id}-${message.timestamp}-${index}`}
              ref={(el) => {
                if (el && message.id && el instanceof HTMLDivElement) {
                  messageRefs.current.set(message.id, el);
                }
              }}
            >
              <ChatMessage
                userAvatar={message.author_avatar || chatDetails?.user_avatar}
                message={message}
                isLastMessage={index === messages.length - 1}
              />
            </Box>
          ))}

          {chatStore.isClosed && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "8px",
                backgroundColor: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.1)",
                alignSelf: "center",
                marginBottom: "25px",
              }}
            >
              <span
                style={{ fontSize: "12px", color: "#888", fontStyle: "italic" }}
              >
                This chat has been closed and is read-only.
              </span>
            </Box>
          )}

          <StatusMessagesRenderer />
        </Column>
      </Box>
    </Box>
  );
});

export default ChatSection;
